import { ReactNode } from 'react';

import { initials, PersonAvatar } from 'components/person-avatar';
import { HUMANOGRAMA } from 'pages/RealizacaoDestaque/components/AgendaDaRealizacao';
import { rostoDoParticipante } from 'pages/RealizacaoDestaque/identidade-do-participante';
import { FOCO_VISIVEL } from 'utils/foco';

import { AcaoEcoa, IntegranteDaAcaoEcoa } from '../acoes-do-semestre';

export interface DetalhesDaAcaoEcoaProps {
  data: AcaoEcoa;
  /** `getFormattedDate` do `useTaskItemEcoa` (dd/MM/yy, como o "Período da entrega" do cartão antigo). */
  getFormattedDate: (valor: any) => string;
  /** Nome de guerra do responsável por apreciar, já formatado. */
  responsavel?: string;
  /** Ações da linha de um integrante (excluir a participação de um liderado), quando o cartão as oferece. */
  acoesDoIntegrante?: (pessoa: IntegranteDaAcaoEcoa) => ReactNode;
}

function Campo({ rotulo, children }: { rotulo: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="mb-1 text-[11px] text-content">{rotulo}</dt>
      <dd className="m-0 text-xs leading-[1.6] text-content">{children}</dd>
    </div>
  );
}

/** Sem nome, o integrante fica onde está (como na ordenação do `TaskItemEcoa`). */
const porNome = (a: IntegranteDaAcaoEcoa, b: IntegranteDaAcaoEcoa) => {
  if (a.name === undefined || b.name === undefined) return 0;
  return a.name < b.name ? -1 : a.name > b.name ? 1 : 0;
};

/** Lotação do integrante como o "Ver mais" do `TaskItemEcoa` mostrava: função - prefixo - dependência. */
export function lotacaoDe(pessoa: IntegranteDaAcaoEcoa): string {
  return `${pessoa.extra?.funcao ?? ''} - ${pessoa.extra?.prefixo ?? ''} - ${pessoa.extra?.nome_dependencia ?? ''}`;
}

/** ECN-10: "Ver mais informações" da Ação ECOA, com os mesmos campos do "Ver mais" do `TaskItemEcoa`. */
export default function DetalhesDaAcaoEcoa({ data, getFormattedDate, responsavel, acoesDoIntegrante }: DetalhesDaAcaoEcoaProps) {
  const integrantes = Array.isArray(data.target) ? [...data.target].sort(porNome) : [];

  return (
    <div className="mt-4 flex flex-col gap-3.5 rounded-jj-lg bg-surface-100 px-5 py-[18px]">
      <dl className="m-0 grid grid-cols-1 gap-3.5 md:grid-cols-2">
        <Campo rotulo="Período da ação">{`${getFormattedDate(data.start)} a ${getFormattedDate(data.end)}`}</Campo>
        {!!responsavel && <Campo rotulo="Responsável por apreciar">{responsavel}</Campo>}
      </dl>

      <dl className="m-0 flex flex-col gap-3.5 border-t border-surface-200 pt-3.5">
        <Campo rotulo="Detalhes da Ação Ecoa">
          <span className="whitespace-pre-wrap">{typeof data.description === 'string' ? data.description : ''}</span>
        </Campo>
        <Campo rotulo="Resultados da Ação Ecoa">
          <span className="whitespace-pre-wrap">{typeof data.result === 'string' ? data.result : ''}</span>
        </Campo>
      </dl>

      {integrantes.length > 0 && (
        <section aria-label="Integrantes que atuaram nesta ação" className="border-t border-surface-200 pt-3.5">
          <h4 className="m-0 mb-2 text-xs font-bold text-brand-blue-dark">{`Integrantes que atuaram nesta ação (${integrantes.length})`}</h4>
          <ul className="m-0 flex list-none flex-col gap-2 p-0">
            {integrantes.map(pessoa => (
              <li key={pessoa._id} data-integrante={pessoa._id} className="rounded-jj-md bg-brand-white px-4 py-3">
                <div className="flex flex-wrap items-center gap-2.5">
                  <PersonAvatar
                    {...rostoDoParticipante(pessoa)}
                    initials={initials(pessoa.extra?.nome_guerra || pessoa._id)}
                    size={36}
                    initialsClassName="bg-brand-green font-display text-[11px] font-bold text-brand-navy"
                  />
                  <div className="min-w-0 flex-1">
                    <a
                      href={`${HUMANOGRAMA}${pessoa._id}`}
                      target="_blank"
                      rel="noreferrer"
                      className={`text-xs font-bold text-link hover:text-link-hover ${FOCO_VISIVEL}`}
                    >
                      {`${pessoa.extra?.nome_guerra ?? pessoa.name ?? ''} (${pessoa._id})`}
                    </a>
                    <p className="m-0 mt-0.5 text-[11px] text-content-soft">{lotacaoDe(pessoa)}</p>
                  </div>
                  {acoesDoIntegrante?.(pessoa)}
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}
    </div>
  );
}
